import axios from 'axios';

export type ToastSeverity = 'success' | 'error' | 'info' | 'warning';

export interface ToastMessage {
  id: string;
  message: string;
  severity: ToastSeverity;
}

type ToastListener = (toast: ToastMessage) => void;

let listeners: ToastListener[] = [];

export function registerToastListener(listener: ToastListener): () => void {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

export function showToast(message: string, severity: ToastSeverity = 'info'): void {
  const toast: ToastMessage = { id: crypto.randomUUID(), message, severity };
  listeners.forEach((listener) => listener(toast));
}

/** Extracts a friendly success message from a React Query mutation's resolved data, if any. */
export function successMessageFrom(data: unknown): string {
  if (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string') {
    return data.message;
  }
  return 'Done.';
}

/** Extracts a friendly error message from an axios/API error, with sensible fallbacks by status. */
export function errorMessageFrom(error: unknown): string {
  if (axios.isAxiosError(error)) {
    // Two shapes arrive here. The app's own Result envelope has `errors` as a string[]. ASP.NET's
    // ValidationProblemDetails — returned when model binding fails BEFORE any of our code runs, e.g.
    // an empty string sent for a Guid? — has `errors` as an object keyed by field name. Only the
    // first was handled, so every binding failure surfaced as "Something went wrong", hiding the one
    // piece of information that would have identified the field.
    const payload = error.response?.data as
      | { errors?: string[] | Record<string, string[]>; message?: string; title?: string; detail?: string }
      | undefined;

    if (Array.isArray(payload?.errors) && payload.errors.length) {
      return payload.errors.join(' ');
    }
    if (payload?.errors && !Array.isArray(payload.errors)) {
      const fieldErrors = Object.entries(payload.errors)
        .map(([field, messages]) => `${field}: ${(messages ?? []).join(' ')}`)
        .filter((line) => line.trim().length > 0);
      if (fieldErrors.length) {
        return fieldErrors.join(' · ');
      }
    }
    if (payload?.message) {
      return payload.message;
    }
    if (payload?.detail || payload?.title) {
      return payload.detail ?? payload.title!;
    }

    switch (error.response?.status) {
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'This record was changed by someone else. Please reload and try again.';
      case undefined:
        return 'Network error — check your connection and try again.';
      default:
        return 'Something went wrong. Please try again.';
    }
  }

  return 'An unexpected error occurred.';
}
